import { EditOutlined } from "@ant-design/icons";
import { ImageNotSupportedOutlined } from "@mui/icons-material";

import { useMutation } from "@tanstack/react-query";
import { Button as AntButton, Space, Table, Tooltip } from "antd";
import moment from "moment";
import GenericTableContainer from "~/components/GenericTableContainer";
import { dateTimeFormat, roles } from "~/config";
import useColumnSearch from "~/hooks/useColumnSearch";
import AuthorAvatar from "~/pages/components/AuthorAvatar";

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useAuthorization from "~/hooks/useAuthorization";
import { getPlantsList } from "~/services/plant.service";
import { getImageUrl, getTableParams } from "~/utils/utils";

const PlantMaster = () => {
  const navigate = useNavigate();
  const { getColumnSearchProps, setSearchText, setSearchedColumn } =
    useColumnSearch();
  const { userHasRequiredRole } = useAuthorization([roles.ADMIN]);

  const [tableParams, setTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 25,
    },
  });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);

    mutate(
      getTableParams({
        pagination: tableParams.pagination,
        filters: selectedKeys[0],
      })
    );
  };

  const {
    data: plants,
    isLoading,
    mutate,
  } = useMutation({
    mutationKey: ["plants"],
    mutationFn: (payloads) => getPlantsList(payloads),
    onSuccess: (data) => {
      // Modify the data response here

      setTableParams({
        ...tableParams,
        pagination: {
          ...tableParams.pagination,
          total: data?.count,
          // 200 is mock data, you should read it from server
          // total: data.totalCount,
        },
      });
      console.log("ON SUCCESS", data);
    },
  });
  const columns = [
    {
      title: "Plant Image",
      key: "Plant_Image",
      responsive: ["lg", "md"],
      width: 150,
      render: (text, record) => {
        return (
          <AuthorAvatar
            image={getImageUrl(record.Plant_Img)}
            icon={
              record.Plant_Img === null ? <ImageNotSupportedOutlined /> : null
            }
          />
        );
      },
      fixed: "left",
      // ...getColumnSearchProps('Plant_Eng_Name', handleSearch, 'Plant Name'),
    },
    {
      title: "Plant English Name",
      dataIndex: "Plant_Eng_Name",
      key: "Plant_Eng_Name",
      responsive: ["lg", "md"],
      width: 250,
      fixed: "left",
      ...getColumnSearchProps("Plant_Eng_Name", handleSearch, "Plant Name"),
      ellipsis: { showTitle: false },

      render: (value) => (
        <Tooltip placement="leftTop" title={value}>
          {value}
        </Tooltip>
      ),
    },
    {
      title: "Plant Local Name",
      dataIndex: "Plant_Local_Name",
      key: "Plant_Local_Name",
      responsive: ["lg", "md"],
      ellipsis: { showTitle: false },

      render: (value) => (
        <Tooltip placement="leftTop" title={value}>
          {value}
        </Tooltip>
      ),
    },
    {
      title: "Plant Scientific Name",
      dataIndex: "Plant_Scientific_Name",
      key: "Plant_Scientific_Name",

      responsive: ["lg", "md"],
      ellipsis: { showTitle: false },

      render: (value) => (
        <Tooltip placement="leftTop" title={value}>
          {value}
        </Tooltip>
      ),
    },

    {
      title: "Plant Category",
      dataIndex: "Plantation_Type",
      key: "Plantation_Type",
      responsive: ["lg", "md"],
      ellipsis: { showTitle: false },

      render: (value) => (
        <Tooltip placement="leftTop" title={value}>
          {value}
        </Tooltip>
      ),
    },
    {
      title: "Plant Season",
      dataIndex: "Plant_Season",
      key: "Plant_Season",
      responsive: ["lg", "md"],
      ellipsis: { showTitle: false },

      render: (value) => (
        <Tooltip placement="leftTop" title={value}>
          {value}
        </Tooltip>
      ),
    },
    {
      title: "Plant Habit",
      dataIndex: "Plant_Habit",
      key: "Plant_Habit",
      responsive: ["lg", "md"],
      ellipsis: { showTitle: false },

      render: (value) => (
        <Tooltip placement="leftTop" title={value}>
          {value}
        </Tooltip>
      ),
    },
    {
      title: "Plant Nature",
      dataIndex: "Plant_Nature",
      key: "Plant_Nature",
      responsive: ["lg", "md"],
      ellipsis: { showTitle: false },

      render: (value) => (
        <Tooltip placement="leftTop" title={value}>
          {value}
        </Tooltip>
      ),
    },
    {
      title: "Plant Description",
      dataIndex: "Plant_Desc",
      key: "Plant_Desc",

      responsive: ["lg", "md"],

      ellipsis: { showTitle: false },
      render: (value) => (
        <Tooltip placement="leftTop" title={value}>
          {value}
        </Tooltip>
      ),
    },
    {
      title: "Plant Uses",
      dataIndex: "Plant_Uses",
      key: "Plant_Uses",

      responsive: ["lg", "md"],

      ellipsis: { showTitle: false },
      render: (value) => (
        <Tooltip placement="leftTop" title={value}>
          {value}
        </Tooltip>
      ),
    },
    {
      title: "Created At",
      dataIndex: "Created_Dt",
      key: "Created_Dt",
      responsive: ["lg", "md"],

      render: (record) =>
        record === null ? "" : moment(record).format(dateTimeFormat),
    },
    {
      title: "Created By",
      dataIndex: "created_by.Login_Name",
      key: "created_by.Login_Name",
      responsive: ["lg", "md"],
    },
    {
      title: "Modified At",
      dataIndex: "Modified_Dt",
      key: "Modified_Dt",
      responsive: ["lg", "md"],

      render: (record) =>
        record === null ? "" : moment(record).format(dateTimeFormat),
    },
    {
      title: "Modified By",
      dataIndex: "modified_by.Login_Name",
      key: "modified_by.Login_Name",
      responsive: ["lg", "md"],
    },
    ...(userHasRequiredRole ? [{
      title: "Action",
      key: "action",
      fixed: "right",
      width: 100,
      render: (rowData, record) => (
        <Space size="middle">
          <Tooltip title="Edit">
            <AntButton
              type="primary"
              shape="circle"
              icon={<EditOutlined />}
              onClick={() =>
                navigate(
                  `/plant-master/update/${rowData?.Plant_Eng_Name.trim().replace(
                    /\s+/g,
                    "-"
                  )}`,
                  {
                    state: rowData,
                  }
                )
              }
            />
          </Tooltip>
        </Space>
      ),
    }] : []),
  ];

  const handleTableChange = (pagination, filters, sorter) => {
    mutate(getTableParams({ pagination }));

    setTableParams({
      pagination,
      filters,
      ...sorter,
    });
  };

  useEffect(() => {
    mutate(getTableParams(tableParams));
  }, []);

  return (
    <GenericTableContainer
      cardHeaderTitle="Plant Master"
      navigateTo="/plant-master/create"
      cardButtonText="Add Plant"
    >
      <Table
        columns={columns}
        scroll={{ x: 2200, y: 350 }}
        // expandable={{ expandedRowRender, defaultExpandedRowKeys: ['0'] }}
        dataSource={plants?.data}
        loading={isLoading}
        pagination={{
          ...tableParams.pagination,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`,
          showSizeChanger: true,
        }}
        onChange={handleTableChange}
      />
    </GenericTableContainer>
  );
};

export default PlantMaster;
